import { createContext, useContext, useState, useEffect, useRef } from 'react'
import { sb, CREDIT_COSTS, PLAN_CREDITS, API_URL } from '../utils/config'

export const AuthContext = createContext(null)

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be inside AuthProvider')
  return ctx
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [monthlyUsage, setMonthlyUsage] = useState(0) // total credits used this month
  const [usageByAction, setUsageByAction] = useState({}) // { script_opus: 450, chat_message: 100, ... }

  const [betaGateEnabled, setBetaGateEnabled] = useState(false)

  // Admin mode  -  unlimited credits for admin testing
  const [adminMode, setAdminModeState] = useState(() => {
    try { return localStorage.getItem('cg_admin_mode') === 'true' } catch { return false }
  })

  function setAdminMode(val) {
    setAdminModeState(val)
    try { localStorage.setItem('cg_admin_mode', val ? 'true' : 'false') } catch {}
  }

  // Plan simulator (admin only) - override the real plan for testing gates
  const [simulatedPlan, setSimulatedPlanState] = useState(() => {
    try { return localStorage.getItem('cg_simulated_plan') || null } catch { return null }
  })

  function setSimulatedPlan(val) {
    setSimulatedPlanState(val)
    try {
      if (val) localStorage.setItem('cg_simulated_plan', val)
      else localStorage.removeItem('cg_simulated_plan')
    } catch {}
  }

  // Credit usage simulator (admin only) - override monthly usage to test quota-based gates
  // Value is a percentage of maxCredits (0 to 100) — null/undefined = no override
  const [simulatedUsagePct, setSimulatedUsagePctState] = useState(() => {
    try {
      const v = localStorage.getItem('cg_simulated_usage_pct')
      return v === null ? null : Number(v)
    } catch { return null }
  })

  function setSimulatedUsagePct(val) {
    setSimulatedUsagePctState(val)
    try {
      if (val == null) localStorage.removeItem('cg_simulated_usage_pct')
      else localStorage.setItem('cg_simulated_usage_pct', String(val))
    } catch {}
  }

  // Check beta gate status (+ places restantes pour le badge public)
  const [betaInfo, setBetaInfo] = useState(null) // { max_spots, taken_spots, remaining, label, is_full }
  useEffect(() => {
    fetch(`${API_URL}/api/beta-gate`).then(r => r.json()).then(d => {
      setBetaGateEnabled(!!d.enabled)
      setBetaInfo({
        max_spots: d.max_spots,
        taken_spots: d.taken_spots,
        remaining: d.remaining,
        label: d.label,
        is_full: d.is_full,
      })
    }).catch(() => {})
  }, [])

  // Notif admin au premier login — idempotent côté backend (flag
  // notified_signup_at). On l'appelle dès qu'on a une session + pas déjà
  // trigger dans cette session navigateur.
  const welcomeSentRef = useRef(false)
  useEffect(() => {
    if (!session?.access_token || welcomeSentRef.current) return
    welcomeSentRef.current = true
    fetch(`${API_URL}/api/me/welcome`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
    }).catch(() => { welcomeSentRef.current = false /* retry on next mount */ })
  }, [session?.access_token])

  // Init: check existing session
  useEffect(() => {
    sb.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s)
      setUser(s?.user || null)
      // Flag global lu par metaPixel.js trackStudioFunnel pour décider
      // de router l'event en path logged-in (CustomEvent → AuthContext) vs
      // path anon (POST /api/track-anon-event). Sans ça les utils ne savent
      // pas si on est loggé sauf à inspecter le localStorage Supabase.
      try { window.__STUDIO_HAS_USER = !!s?.user } catch {}
      if (s?.user) fetchUserProfile(s.user.id)
      else setLoading(false)
    })

    const { data: { subscription } } = sb.auth.onAuthStateChange((_event, s) => {
      setSession(s)
      setUser(s?.user || null)
      try { window.__STUDIO_HAS_USER = !!s?.user } catch {}
      if (s?.user) fetchUserProfile(s.user.id)
      else {
        setUserProfile(null)
        setMonthlyUsage(0)
        setUsageByAction({})
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  // Re-fetch le profil quand l'onglet redevient actif : le plan payé via
  // Stripe (webhook → users.studio_plan) doit apparaître SANS reload manuel.
  // Cas réel (03/07) : paiement validé à 8h34, paywall remontré à 8h54 dans
  // l'onglet resté ouvert → la cliente a cru qu'on lui redemandait de payer.
  useEffect(() => {
    function onActive() {
      if (document.visibilityState === 'visible' && user?.id) fetchUserProfile(user.id)
    }
    document.addEventListener('visibilitychange', onActive)
    window.addEventListener('focus', onActive)
    return () => {
      document.removeEventListener('visibilitychange', onActive)
      window.removeEventListener('focus', onActive)
    }
  }, [user?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // Verticale du COMPTE (users.vertical, posée au scan verticalisé) : reposée
  // en localStorage à chaque login pour que l'enveloppe (Studio Sport...) suive
  // l'utilisateur sur n'importe quel appareil ou navigateur.
  function syncVerticalFromProfile(profile) {
    try {
      if (profile?.vertical) localStorage.setItem('studio_vertical_persona', profile.vertical)
    } catch { /* noop */ }
  }

  async function fetchUserProfile(userId) {
    try {
      const { data, error } = await sb.from('users').select('*').eq('id', userId).single()
      if (error) {
        await new Promise(r => setTimeout(r, 2000))
        const { data: retry } = await sb.from('users').select('*').eq('id', userId).single()
        if (retry) { setUserProfile(retry); syncVerticalFromProfile(retry) }
      } else {
        setUserProfile(data)
        syncVerticalFromProfile(data)
      }
      await refreshUsage(userId)
    } catch (e) {
      console.warn('[Auth] fetchUserProfile error:', e.message)
    }
    setLoading(false)
  }

  // Refresh usage — sum credits per action.
  // Free users : LIFETIME (1000 cr one-time, pas renouvelables — discovery budget).
  // Paid users : MONTHLY (renouvelable chaque mois).
  // Le filtrage se fait selon le plan de l'user (lu dans userProfile).
  async function refreshUsage(userId) {
    try {
      const uid = userId || user?.id
      if (!uid) return
      // Détecte le plan du user — si on n'a pas encore le profil, fallback 'free'.
      const userPlanForUsage = userProfile?.plan || 'free'

      let query = sb
        .from('usage_tracking')
        .select('action, credits_used')
        .eq('user_id', uid)

      if (userPlanForUsage !== 'free') {
        // Paid : compteur mensuel
        const now = new Date()
        const periodStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
        query = query.gte('period_start', periodStart)
      }
      // Free : pas de filtre période → lifetime

      const { data } = await query

      if (data) {
        let total = 0
        const byAction = {}
        for (const row of data) {
          const c = row.credits_used || 0
          total += c
          byAction[row.action] = (byAction[row.action] || 0) + c
        }
        setMonthlyUsage(total)
        setUsageByAction(byAction)
      }
    } catch (e) {
      console.warn('[Auth] refreshUsage error:', e.message)
    }
  }

  // Plan-based credit limit
  // When a plan is simulated (admin testing), it overrides the real plan AND disables the adminMode bypass
  // SÉCURITÉ : les flags admin (simulatedPlan, simulatedUsagePct, adminMode) ne
  // s'appliquent QUE si l'user est l'admin réel. Sinon un user normal pourrait
  // coller 'cg_simulated_plan=pro' dans son localStorage et voir l'UI premium.
  // Liste des emails admin. Ajoute ici tout compte qui doit avoir accès aux
  // outils admin (test mode, refresh insights, simulate plan, etc.). Inclut
  // les comptes secondaires utilisés pour tester les flows newbie.
  const ADMIN_EMAILS = [
    'antonin.girardeau.pro@gmail.com',
    'famous_77@hotmail.fr',
  ]
  const realIsAdmin = !!user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())
  const realPlan = userProfile?.plan || 'free'
  // simulatedPlan / simulatedUsagePct / adminMode : ignorés si non-admin
  const effectiveSimulatedPlan = realIsAdmin ? simulatedPlan : null
  const effectiveSimulatedUsagePct = realIsAdmin ? simulatedUsagePct : null
  const plan = effectiveSimulatedPlan || realPlan
  const isSimulating = !!effectiveSimulatedPlan
  const isSimulatingUsage = effectiveSimulatedUsagePct != null
  const maxCredits = PLAN_CREDITS[plan] ?? 1000
  const isUnlimitedPlan = maxCredits === -1
  // When simulating a plan, we want to see the app as that plan would see it, so adminMode is disabled.
  // adminMode est aussi conditionné à realIsAdmin (sinon un user normal avec
  // cg_admin_mode=true en localStorage aurait des crédits illimités via canAfford).
  const effectiveAdminMode = realIsAdmin && adminMode && !isSimulating && !isSimulatingUsage
  // Effective monthly usage (simulated or real)
  const effectiveMonthlyUsage = isSimulatingUsage && !isUnlimitedPlan
    ? Math.round((Number(effectiveSimulatedUsagePct) / 100) * maxCredits)
    : monthlyUsage

  // Can the user afford this action ?
  // Modèle freemium classique : tous les plans (incluant free) sont gated
  // sur les crédits mensuels uniquement. Free = 1000 cr/mois renouvelables.
  function canAfford(action) {
    if (effectiveAdminMode || isUnlimitedPlan) return true
    const cost = CREDIT_COSTS[action] || 0
    return (effectiveMonthlyUsage + cost) <= maxCredits
  }

  // Legacy compat  -  can do anything?
  function canGenerate() {
    if (effectiveAdminMode || isUnlimitedPlan) return true
    return effectiveMonthlyUsage < maxCredits
  }

  // Get remaining credits
  function remainingCredits() {
    if (effectiveAdminMode || isUnlimitedPlan) return Infinity
    return Math.max(0, maxCredits - effectiveMonthlyUsage)
  }

  // Usage percentage (0-100), useful for quota gauges and color thresholds
  function usagePct() {
    if (effectiveAdminMode || isUnlimitedPlan) return 0
    if (maxCredits <= 0) return 100
    return Math.min(100, Math.round((effectiveMonthlyUsage / maxCredits) * 100))
  }

  // Track a usage action with proper credit cost
  // Note: always tracks, even in admin mode — admin mode only bypasses canAfford checks
  async function trackUsage(action, creditsOverride = null) {
    try {
      if (!user) return

      const cost = creditsOverride ?? (CREDIT_COSTS[action] || 0)
      if (cost === 0) return

      const now = new Date()
      const periodStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
      await sb.from('usage_tracking').insert({
        user_id: user.id,
        action,
        credits_used: cost,
        period_start: periodStart,
      })
      // Update local state immediately
      setMonthlyUsage(prev => prev + cost)
      setUsageByAction(prev => ({ ...prev, [action]: (prev[action] || 0) + cost }))
    } catch (e) {
      console.warn('[Auth] trackUsage error:', e.message)
    }
  }

  // ────────────────────────────────────────────────────────────
  // Unified event tracking — logs every meaningful user action
  // to the `user_events` table (decoupled from billing tracking).
  // Used for beta analytics: page views, script events, chat
  // messages, format swipes, etc.
  //
  // Usage: trackEvent('script_generated', { video_id, word_count, ... })
  // ────────────────────────────────────────────────────────────
  const EVENT_CATEGORIES = {
    script_generated: 'script', script_saved: 'script', script_polished: 'script',
    script_regenerated: 'script', script_edited: 'script', script_copied: 'script',
    script_evaluated: 'script',
    format_searched: 'search', format_chosen: 'search', format_saved: 'search',
    format_swiped_right: 'search', format_swiped_left: 'search', format_viewed: 'search',
    chat_message_sent: 'chat', chat_opened: 'chat', chat_closed: 'chat',
    video_analysis_opened: 'video', video_played: 'video',
    profile_analyzed: 'profile', audience_synthesized: 'profile',
    page_view: 'navigation', feedback_given: 'feedback',
    phase_entered: 'navigation', phase_exited: 'navigation',
  }

  // One session_id per browser tab life (survives reloads via sessionStorage)
  function getSessionId() {
    try {
      let sid = sessionStorage.getItem('cg_session_id')
      if (!sid) {
        sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
        sessionStorage.setItem('cg_session_id', sid)
      }
      return sid
    } catch {
      return `sess_${Date.now()}_nostorage`
    }
  }

  async function trackEvent(eventType, metadata = {}, opts = {}) {
    try {
      if (!user) return
      if (!eventType) return
      const row = {
        user_id: user.id,
        session_id: getSessionId(),
        event_type: eventType,
        event_category: opts.category || EVENT_CATEGORIES[eventType] || null,
        page: opts.page || (typeof window !== 'undefined' ? (window.location.hash.replace('#', '') || window.location.pathname) : null),
        metadata: metadata && typeof metadata === 'object' ? metadata : {},
        duration_ms: opts.durationMs ?? null,
      }
      // Fire-and-forget : analytics jamais bloquant pour l'UI
      sb.from('user_events').insert(row).then(({ error }) => {
        if (error) console.warn('[trackEvent] insert error:', error.message)
      })
    } catch (e) {
      // Analytics must NEVER throw
      console.warn('[trackEvent] failed (non-blocking):', e.message)
    }
  }

  // Listener global : les events studioEvents (utils/metaPixel.js) émettent
  // une CustomEvent qu'on capte ici pour les persister dans user_events.
  // Permet de query le parcours user depuis le backend sans dépendre de GA4.
  useEffect(() => {
    if (typeof window === 'undefined') return
    function handler(e) {
      const { eventType, metadata } = e.detail || {}
      if (eventType) trackEvent(eventType, metadata || {}, { category: 'studio_funnel' })
    }
    window.addEventListener('studio:track-event', handler)
    return () => window.removeEventListener('studio:track-event', handler)
  }, [user?.id])

  // Auth methods
  async function signInWithEmail(email, password) {
    const { data, error } = await sb.auth.signInWithPassword({ email, password })
    if (error) throw error
    return data
  }

  async function signUpWithEmail(email, password, displayName = '', { consent = false, dailyOptin = true } = {}) {
    const { data, error } = await sb.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: displayName || email.split('@')[0] },
        // Sans ça, le lien de confirmation renvoie au Site URL Supabase
        // (Merlin classique) : l'inscrit Studio atterrissait sur l'onboarding
        // chatbot. Bug repéré par Antonin le 2026-07-07.
        emailRedirectTo: `${window.location.origin}/?studio=1`,
      }
    })
    if (error) throw error
    // Stamp consent timestamp + opt-in email quotidien sur public.users dès que
    // la ligne auth existe (mirror créé par trigger DB). Si le mirror n'a pas
    // rattrapé (race), l'UPDATE est un no-op — réessayé au prochain load.
    if (data.user?.id) {
      const patch = { daily_email_optin: !!dailyOptin }
      if (consent) patch.consent_given_at = new Date().toISOString()
      try {
        await sb.from('users').update(patch).eq('id', data.user.id)
      } catch (e) {
        console.warn('[Auth] users update failed (trigger may not have created the row yet):', e.message)
      }
    }
    return data
  }

  // Magic link : connexion ET inscription par email, sans mot de passe.
  // Email inconnu → compte créé ; email connu → reconnecté. Les échecs
  // "already registered" / "invalid credentials" disparaissent par
  // construction, et ça marche dans la webview Instagram (pas d'OAuth).
  // Consentement CGU + opt-in daily : stashés en localStorage, appliqués
  // sur public.users au retour du lien (effet global StudioPage).
  async function signInWithMagicLink(email, { displayName = '', consent = false, dailyOptin = true, redirectTo = '' } = {}) {
    try {
      localStorage.setItem('studio_magic_meta', JSON.stringify({ consent, dailyOptin, at: new Date().toISOString() }))
      localStorage.setItem('studio_magic_pending', '1')
    } catch { /* stockage best effort */ }
    const { error } = await sb.auth.signInWithOtp({
      email,
      options: {
        shouldCreateUser: true,
        // redirectTo custom : porte ?claim=<anon_id> pour rattacher le scan
        // invité même si le lien s'ouvre dans un AUTRE navigateur que celui
        // qui l'a demandé (webview Instagram → Safari : stockage cloisonné).
        emailRedirectTo: redirectTo || `${window.location.origin}/?studio=1`,
        // Consentement + opt-in dans user_metadata : le stash localStorage
        // ci-dessus ne survit pas au changement de navigateur.
        data: {
          full_name: displayName || email.split('@')[0],
          studio_magic_consent_at: consent ? new Date().toISOString() : null,
          studio_daily_optin: !!dailyOptin,
        },
      },
    })
    if (error) throw error
    return true
  }

  // Code à 6 chiffres du même envoi ({{ .Token }} dans le template email) :
  // secours quand l'user ne peut/veut pas cliquer le lien. Il tape le code
  // et reste dans le navigateur courant, webview comprise.
  async function verifyEmailOtp(email, token) {
    const { data, error } = await sb.auth.verifyOtp({ email, token: String(token || '').trim(), type: 'email' })
    if (error) throw error
    return data
  }

  async function signInWithGoogle() {
    // Si on est sur studio.* et que la whitelist Supabase ne contient pas
    // ce domaine, Supabase fallback vers Site URL (= Merlin). Pour récupérer
    // l'user sur Studio quand même, on pose un cookie partagé sur le domaine
    // parent .merlin-content-ai.com avant l'OAuth — Merlin le lira et fera
    // le transfert de session vers Studio via URL hash (cf. studioRedirectFix).
    try {
      const isStudio = window.location.hostname.startsWith('studio.')
      if (isStudio && window.location.hostname.endsWith('merlin-content-ai.com')) {
        document.cookie = 'studio_oauth_intent=1; domain=.merlin-content-ai.com; path=/; max-age=600; samesite=lax; secure'
      }
    } catch {}
    const { data, error } = await sb.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    })
    if (error) throw error
    return data
  }

  async function signOut() {
    await sb.auth.signOut()
    setUser(null)
    setUserProfile(null)
    setSession(null)
    setMonthlyUsage(0)
    setUsageByAction({})
  }

  const isAdmin = !!user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase())
  const isApproved = !!userProfile?.approved
  const isRejected = !!userProfile?.rejected_at
  const isPaidPlan = plan !== 'free'
  // When simulating, plan-level FEATURE gates respect the simulated plan
  // (=> admin bypasses disabled for canAfford/useGate).
  const isAdminForGates = isAdmin && !isSimulating
  // ACCESS gates (rejection / waitlist / needsPlan) en revanche restent
  // bypassées pour l'admin même en simulation — sinon impossible de tester
  // le flow free-plan : l'admin tomberait sur le mur "choose plan" avant
  // d'avoir accès à l'app pour valider le gating UI/quota.
  const isAdminForAccessGates = isAdmin
  // 3-state gating (rejection wins over everything, then approval, then plan):
  //   rejected → show rejection page, stop
  //   beta on + not approved → show "pending validation" waitlist
  //   beta on + approved + plan=free → show "choose plan" (paiement obligatoire en béta)
  //   beta off → plan Free OK (accès libre normal)
  // Sur dev : le mur "choose plan" est désactivé pour pouvoir tester le
  // parcours free (sign-up → onboarding → library avec gating). En prod il
  // reste actif tant qu'on ne décide pas définitivement de basculer le free.
  const isDevEnv = import.meta.env.VITE_APP_ENV === 'dev'
  const needsRejectionNotice = isRejected && !isAdminForAccessGates
  const needsApproval = !needsRejectionNotice && betaGateEnabled && !isApproved && !isAdminForAccessGates
  const needsPlan = !isDevEnv && betaGateEnabled && !needsRejectionNotice && !needsApproval && !isPaidPlan && !isAdminForAccessGates

  return (
    <AuthContext.Provider value={{
      user,
      userProfile,
      session,
      loading,
      plan,
      realPlan,
      simulatedPlan,
      setSimulatedPlan,
      isSimulating,
      simulatedUsagePct,
      setSimulatedUsagePct,
      isSimulatingUsage,
      monthlyUsage: effectiveMonthlyUsage,
      realMonthlyUsage: monthlyUsage,
      usageByAction,
      maxCredits,
      isUnlimited: isUnlimitedPlan || effectiveAdminMode,
      usagePct,
      isAdmin,
      isApproved,
      isRejected,
      isPaidPlan,
      needsPlan,
      needsApproval,
      needsRejectionNotice,
      betaGateEnabled,
      betaInfo,
      adminMode: effectiveAdminMode,
      setAdminMode,
      canGenerate,
      canAfford,
      remainingCredits,
      trackUsage,
      trackEvent,
      refreshUsage,
      signInWithEmail,
      signUpWithEmail,
      signInWithGoogle,
      signInWithMagicLink,
      verifyEmailOtp,
      signOut,
      // ── Studio (produit séparé) ──
      // Expose studio_plan. Résolution multi-source :
      //   1. localStorage override (dev/admin testing, prend immédiatement effet)
      //   2. users.studio_plan (table publique, source canon en prod)
      //   3. user_metadata fallback (set via supabase.auth.updateUser)
      studioPlan: (() => {
        try {
          const override = typeof window !== 'undefined' && localStorage.getItem('studio_plan_dev_override')
          if (override) return override
        } catch {}
        return userProfile?.studio_plan || user?.user_metadata?.studio_plan || null
      })(),
      // Permet aux composants de forcer un re-fetch de userProfile après une
      // action backend (paiement Stripe, admin toggle, etc.) sans recharger
      // toute la page.
      refreshProfile: async () => {
        if (user?.id) await fetchUserProfile(user.id);
      },
    }}>
      {children}
    </AuthContext.Provider>
  )
}
